/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";

export type ListRiskResultsSecurityOption1 = {
  apikeyHeaderGramKey: string;
  projectSlugHeaderGramProject: string;
};

export type ListRiskResultsSecurityOption2 = {
  projectSlugHeaderGramProject: string;
  sessionHeaderGramSession: string;
};

export type ListRiskResultsSecurity = {
  option1?: ListRiskResultsSecurityOption1 | undefined;
  option2?: ListRiskResultsSecurityOption2 | undefined;
};

export type ListRiskResultsRequest = {
  /**
   * Optional policy ID to filter by.
   */
  policyId?: string | undefined;
  /**
   * Optional chat ID to filter by.
   */
  chatId?: string | undefined;
  /**
   * Optional concrete MCP server ID to match exactly.
   */
  mcpServerId?: string | undefined;
  /**
   * Optional risk result ID; returns that one finding even when it is not on a loaded page, such as from a shared link. A dismissed finding, such as a false positive, is not returned.
   */
  resultId?: string | undefined;
  /**
   * Optional ID of one mediated MCP execution (tool call, resource read or prompt get), the execution_id a result carries; returns the live findings on that execution across its request and response phases. Findings that were dismissed, auto-excluded by exclusion rules, or raised under deleted policies are omitted.
   */
  executionId?: string | undefined;
  /**
   * Optional rule category key to filter by (e.g. secrets, pii, financial).
   */
  category?: string | undefined;
  /**
   * Optional rule identifier substring to filter by (case-insensitive, e.g. 'secret' matches all 'secret.*' rules).
   */
  ruleId?: string | undefined;
  /**
   * Optional user identifier substring to filter by (case-insensitive, matched against the chat's external user id).
   */
  userId?: string | undefined;
  /**
   * Optional external user identifiers to filter by, matched whole rather than as a substring. Pass every identifier a subject is known by; unlike user_id this cannot pull in another person whose identifier merely contains theirs.
   */
  externalUserIds?: Array<string> | undefined;
  /**
   * If true, collapse results to one row per (policy_id, rule_id, match), keeping the most recent occurrence. Useful when the same secret is detected many times within a single message body.
   */
  uniqueMatch?: boolean | undefined;
  /**
   * If true, only return findings from chats that are not linked to an assistant. Useful for surfacing events that are missing user attribution.
   */
  nonAssistant?: boolean | undefined;
  /**
   * Optional assistant ID; only return findings from chats linked to this assistant.
   */
  assistantId?: string | undefined;
  /**
   * Filter results to messages created at or after this timestamp (ISO 8601).
   */
  from?: Date | undefined;
  /**
   * Filter results to messages created strictly before this timestamp (ISO 8601).
   */
  to?: Date | undefined;
  /**
   * Cursor to fetch the next page of results.
   */
  cursor?: string | undefined;
  /**
   * Maximum number of results to return per page.
   */
  limit?: number | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
  /**
   * project header
   */
  gramProject?: string | undefined;
};

/** @internal */
export type ListRiskResultsSecurityOption1$Outbound = {
  "apikey_header_Gram-Key": string;
  "project_slug_header_Gram-Project": string;
};

/** @internal */
export const ListRiskResultsSecurityOption1$outboundSchema: z.ZodMiniType<
  ListRiskResultsSecurityOption1$Outbound,
  ListRiskResultsSecurityOption1
> = z.pipe(
  z.object({
    apikeyHeaderGramKey: z.string(),
    projectSlugHeaderGramProject: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      apikeyHeaderGramKey: "apikey_header_Gram-Key",
      projectSlugHeaderGramProject: "project_slug_header_Gram-Project",
    });
  }),
);

export function listRiskResultsSecurityOption1ToJSON(
  listRiskResultsSecurityOption1: ListRiskResultsSecurityOption1,
): string {
  return JSON.stringify(
    ListRiskResultsSecurityOption1$outboundSchema.parse(
      listRiskResultsSecurityOption1,
    ),
  );
}

/** @internal */
export type ListRiskResultsSecurityOption2$Outbound = {
  "project_slug_header_Gram-Project": string;
  "session_header_Gram-Session": string;
};

/** @internal */
export const ListRiskResultsSecurityOption2$outboundSchema: z.ZodMiniType<
  ListRiskResultsSecurityOption2$Outbound,
  ListRiskResultsSecurityOption2
> = z.pipe(
  z.object({
    projectSlugHeaderGramProject: z.string(),
    sessionHeaderGramSession: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      projectSlugHeaderGramProject: "project_slug_header_Gram-Project",
      sessionHeaderGramSession: "session_header_Gram-Session",
    });
  }),
);

export function listRiskResultsSecurityOption2ToJSON(
  listRiskResultsSecurityOption2: ListRiskResultsSecurityOption2,
): string {
  return JSON.stringify(
    ListRiskResultsSecurityOption2$outboundSchema.parse(
      listRiskResultsSecurityOption2,
    ),
  );
}

/** @internal */
export type ListRiskResultsSecurity$Outbound = {
  Option1?: ListRiskResultsSecurityOption1$Outbound | undefined;
  Option2?: ListRiskResultsSecurityOption2$Outbound | undefined;
};

/** @internal */
export const ListRiskResultsSecurity$outboundSchema: z.ZodMiniType<
  ListRiskResultsSecurity$Outbound,
  ListRiskResultsSecurity
> = z.pipe(
  z.object({
    option1: z.optional(
      z.lazy(() => ListRiskResultsSecurityOption1$outboundSchema),
    ),
    option2: z.optional(
      z.lazy(() => ListRiskResultsSecurityOption2$outboundSchema),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      option1: "Option1",
      option2: "Option2",
    });
  }),
);

export function listRiskResultsSecurityToJSON(
  listRiskResultsSecurity: ListRiskResultsSecurity,
): string {
  return JSON.stringify(
    ListRiskResultsSecurity$outboundSchema.parse(listRiskResultsSecurity),
  );
}

/** @internal */
export type ListRiskResultsRequest$Outbound = {
  policy_id?: string | undefined;
  chat_id?: string | undefined;
  mcp_server_id?: string | undefined;
  result_id?: string | undefined;
  execution_id?: string | undefined;
  category?: string | undefined;
  rule_id?: string | undefined;
  user_id?: string | undefined;
  external_user_ids?: Array<string> | undefined;
  unique_match?: boolean | undefined;
  non_assistant?: boolean | undefined;
  assistant_id?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
  "Gram-Key"?: string | undefined;
  "Gram-Session"?: string | undefined;
  "Gram-Project"?: string | undefined;
};

/** @internal */
export const ListRiskResultsRequest$outboundSchema: z.ZodMiniType<
  ListRiskResultsRequest$Outbound,
  ListRiskResultsRequest
> = z.pipe(
  z.object({
    policyId: z.optional(z.string()),
    chatId: z.optional(z.string()),
    mcpServerId: z.optional(z.string()),
    resultId: z.optional(z.string()),
    executionId: z.optional(z.string()),
    category: z.optional(z.string()),
    ruleId: z.optional(z.string()),
    userId: z.optional(z.string()),
    externalUserIds: z.optional(z.array(z.string())),
    uniqueMatch: z.optional(z.boolean()),
    nonAssistant: z.optional(z.boolean()),
    assistantId: z.optional(z.string()),
    from: z.optional(z.pipe(z.date(), z.transform(v => v.toISOString()))),
    to: z.optional(z.pipe(z.date(), z.transform(v => v.toISOString()))),
    cursor: z.optional(z.string()),
    limit: z.optional(z.int()),
    gramKey: z.optional(z.string()),
    gramSession: z.optional(z.string()),
    gramProject: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      policyId: "policy_id",
      chatId: "chat_id",
      mcpServerId: "mcp_server_id",
      resultId: "result_id",
      executionId: "execution_id",
      ruleId: "rule_id",
      userId: "user_id",
      externalUserIds: "external_user_ids",
      uniqueMatch: "unique_match",
      nonAssistant: "non_assistant",
      assistantId: "assistant_id",
      gramKey: "Gram-Key",
      gramSession: "Gram-Session",
      gramProject: "Gram-Project",
    });
  }),
);

export function listRiskResultsRequestToJSON(
  listRiskResultsRequest: ListRiskResultsRequest,
): string {
  return JSON.stringify(
    ListRiskResultsRequest$outboundSchema.parse(listRiskResultsRequest),
  );
}
